import {FC, useEffect, useState} from "react";
import {Button, Icon} from "semantic-ui-react";
import {FeedbackAction, IInsightAction, ISpeedOfServiceInsight} from "../api/coachInsightsModels";
import {formatHour, getInsightKey, getInsightSeverity} from "../api/coachInsightsUtils";
import insightActionsStore from "../api/insightActionsStore";
import ImpactedExperiencesModal from "./ImpactedExperiencesModal";
import SosBusinessImpactModal from "./SosBusinessImpactModal";
import DriveThruReplayModal from "./DriveThruReplayModal";
import StaffCoverageModal from "./StaffCoverageModal";
import applicationApi from "../../application/api/applicationAPI";
import hj from "../../../tracking/hotjar";

interface IProps {
    insight: ISpeedOfServiceInsight;
    onActionRecorded: () => void;
}

function formatRoi(roi: number): string {
    if (roi >= 1000) return `$${(roi / 1000).toFixed(1)}k`;
    return `$${roi}`;
}

const TRIAGE_OPTIONS: {action: FeedbackAction; label: string; icon: string}[] = [
    {action: "AssignToRGM", label: "Assign to RGM", icon: "user plus"},
    {action: "ContactRGMNow", label: "Contact RGM", icon: "phone"}
];

const InsightActionRow: FC<IProps> = ({insight, onActionRecorded}) => {
    const [savedAction, setSavedAction] = useState<IInsightAction | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [impactedOpen, setImpactedOpen] = useState(false);
    const [businessImpactOpen, setBusinessImpactOpen] = useState(false);
    const [replayOpen, setReplayOpen] = useState(false);
    const [staffCoverageOpen, setStaffCoverageOpen] = useState(false);

    const insightKey = getInsightKey(insight);
    const severity = getInsightSeverity(insight);

    useEffect(() => {
        insightActionsStore.getActionForInsight(insightKey).then(setSavedAction);
    }, [insightKey]);

    const handleTriage = async (action: FeedbackAction) => {
        if (savedAction?.actionType === action) {
            await insightActionsStore.dismissAction(insightKey);
            setSavedAction(null);
            onActionRecorded();
            return;
        }
        const record = await insightActionsStore.recordAction(
            {
                insightKey,
                locationName: insight.locationName,
                locationPk: insight.locationPk,
                businessDate: insight.businessDate,
                dayPart: insight.dayPart,
                title: insight.title,
                category: insight.category,
                estimatedRoi: insight.estimatedRoi,
                averageSOS: insight.averageSOS,
                targetSOS: insight.targetSOS,
                impactedExperiences: insight.impactedExperiences,
                timeframeStartHour: insight.timeframeStartHour,
                timeframeEndHour: insight.timeframeEndHour,
                whoToFollowUpWith: insight.whoToFollowUpWith
            },
            action
        );
        setSavedAction(record);
        onActionRecorded();
        applicationApi.trackPageViewOrAction(
            window.location.pathname,
            `feature=InsightAction&action=${action}`
        );
        hj("event", "insight_action_recorded");
    };

    const handleDismiss = async () => {
        await insightActionsStore.dismissAction(insightKey);
        setSavedAction(null);
        setDismissed(true);
        onActionRecorded();
        applicationApi.trackPageViewOrAction(
            window.location.pathname,
            "feature=InsightAction&action=Dismissed"
        );
        hj("event", "insight_action_dismissed");
    };

    if (dismissed) return null;

    const timeRange =
        insight.timeframeStartHour === insight.timeframeEndHour
            ? formatHour(insight.timeframeStartHour)
            : `${formatHour(insight.timeframeStartHour)}–${formatHour(insight.timeframeEndHour)}`;

    return (
        <div className={`action-row severity-${severity}${savedAction ? " actioned" : ""}`}>
            <div className="action-row-severity-bar" />

            <div className="action-row-body">
                <div className="action-row-roi">
                    <span className="roi-value">
                        {insight.estimatedRoi > 0 ? formatRoi(insight.estimatedRoi) : "—"}
                    </span>
                    <span className="roi-label">est. recoverable</span>
                </div>

                <div className="action-row-context">
                    <span className="action-row-title">{insight.title}</span>
                    <div className="action-row-meta">
                        <span className="meta-location">{insight.locationName}</span>
                        <span className="meta-sep">&bull;</span>
                        <span className="meta-daypart">{insight.dayPart}</span>
                        <span className="meta-sep">&bull;</span>
                        <span className="meta-time">{timeRange}</span>
                        <span className="sos-chip">
                            {insight.averageSOS} / {insight.targetSOS}
                        </span>
                        <span className="guests-chip">
                            {insight.impactedExperiences} guests
                        </span>
                    </div>
                    <div className="action-row-next-step">
                        <Icon name="lightbulb outline" className="next-step-icon" />
                        <span>{insight.recommendedAction}</span>
                    </div>
                </div>

                <div className="action-row-controls">
                    <div className="triage-buttons">
                        {TRIAGE_OPTIONS.map(({action, label, icon}) => (
                            <Button
                                key={action}
                                size="small"
                                className={`triage-btn${savedAction?.actionType === action ? " active" : ""}`}
                                onClick={() => handleTriage(action)}
                            >
                                <Icon name={icon as any} />
                                {label}
                            </Button>
                        ))}
                        <Button
                            size="small"
                            className="triage-btn dismiss-btn"
                            onClick={handleDismiss}
                        >
                            <Icon name="times" />
                            Dismiss
                        </Button>
                    </div>

                    <div className="deep-dive-buttons">
                        <Button
                            size="mini"
                            basic
                            className="deep-dive-btn"
                            onClick={() => {
                                applicationApi.trackPageViewOrAction(
                                    window.location.pathname,
                                    "feature=ImpactedExperiences"
                                );
                                hj("event", "speed_of_service_impacted_experiences_opened");
                                setImpactedOpen(true);
                            }}
                        >
                            <Icon name="list alternate" />
                            Transactions
                        </Button>
                        <Button
                            size="mini"
                            basic
                            className="deep-dive-btn"
                            onClick={() => {
                                applicationApi.trackPageViewOrAction(
                                    window.location.pathname,
                                    "feature=BusinessImpact"
                                );
                                hj("event", "speed_of_service_business_impact_opened");
                                setBusinessImpactOpen(true);
                            }}
                        >
                            <Icon name="line graph" />
                            Business Impact
                        </Button>
                        <Button
                            size="mini"
                            basic
                            className="deep-dive-btn"
                            onClick={() => {
                                applicationApi.trackPageViewOrAction(
                                    window.location.pathname,
                                    "feature=DriveThruReplay"
                                );
                                hj("event", "drive_thru_replay_opened");
                                setReplayOpen(true);
                            }}
                        >
                            <Icon name="play circle" />
                            Replay
                        </Button>
                        <Button
                            size="mini"
                            basic
                            className="deep-dive-btn"
                            onClick={() => {
                                applicationApi.trackPageViewOrAction(
                                    window.location.pathname,
                                    "feature=StaffCoverage"
                                );
                                hj("event", "staff_coverage_opened");
                                setStaffCoverageOpen(true);
                            }}
                        >
                            <Icon name="users" />
                            Staff Coverage
                        </Button>
                    </div>
                </div>
            </div>

            <ImpactedExperiencesModal
                open={impactedOpen}
                onClose={() => setImpactedOpen(false)}
                locationPk={insight.locationPk}
                businessDate={insight.businessDate}
                locationName={insight.locationName}
                timeframeStartHour={insight.timeframeStartHour}
                timeframeEndHour={insight.timeframeEndHour}
            />
            <SosBusinessImpactModal
                open={businessImpactOpen}
                onClose={() => setBusinessImpactOpen(false)}
                locationPk={insight.locationPk}
                businessDate={insight.businessDate}
                locationName={insight.locationName}
                timeframeStartHour={insight.timeframeStartHour}
                timeframeEndHour={insight.timeframeEndHour}
            />
            <DriveThruReplayModal
                isOpen={replayOpen}
                onClose={() => setReplayOpen(false)}
                locationPk={insight.locationPk}
                locationName={insight.locationName}
                businessDate={insight.businessDate}
                timeframeStartHour={insight.timeframeStartHour}
                timeframeEndHour={insight.timeframeEndHour}
            />
            <StaffCoverageModal
                open={staffCoverageOpen}
                onClose={() => setStaffCoverageOpen(false)}
                locationPk={insight.locationPk}
                businessDate={insight.businessDate}
            />
        </div>
    );
};

export default InsightActionRow;
